import { useMutation } from '@apollo/client';
import useStore from '@/stores/useStore';
import useInstantStylingStore from '@/stores/useInstantStylingStore';
import {
  MutationResult,
  MutationToggleInstantActiveArgs,
} from '@/shared/types/generated';
import { TOGGLE_INSTANT_ACTIVE } from '@/shared/mutations';
import SentryService from '@/services/sentry/Sentry.service';

export function useInstantStylingToggle() {
  const setSnackbarState = useStore((state) => state.setSnackbarState);
  const isInstantStylingActive = useInstantStylingStore(
    (state) => state.isInstantStylingActive,
  );
  const setIsInstantStylingActive = useInstantStylingStore(
    (state) => state.setIsInstantStylingActive,
  );

  const [toggleInstantActive] = useMutation<
    MutationResult<'toggleInstantActive'>,
    MutationToggleInstantActiveArgs
  >(TOGGLE_INSTANT_ACTIVE);

  /**
   * Toggles the instant styling flag on the server,
   * updates instant styling store, and returns the new state.
   */
  async function toggleInstantStyling(): Promise<boolean> {
    const toggledInstantStylingState = !isInstantStylingActive;

    try {
      const { data } = await toggleInstantActive({
        variables: { input: { isActive: toggledInstantStylingState } },
      });

      const newInstantStylingState = data?.toggleInstantActive.isActive;
      if (newInstantStylingState === undefined) {
        throw new Error('No instant styling state found in server response');
      }

      setIsInstantStylingActive(newInstantStylingState);
      return newInstantStylingState;
    } catch (error) {
      SentryService.captureError('Error toggling instant active', error);
      setSnackbarState({
        open: true,
        message:
          'An error occurred while changing your instant styling status. Please try again.',
        severity: 'error',
      });

      // Return the previous state if the mutation fails
      return isInstantStylingActive;
    }
  }

  return { isInstantStylingActive, toggleInstantStyling };
}
